/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useEffect, useRef, useState, useCallback
} from 'react';
import debounce from 'lodash/debounce';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import dialogPolyfill from 'dialog-polyfill';
import classes from './classes.module.scss';
import { appGeneralPopupTheme } from '../config/const';
import {
  hideBodyScroll, unlockBodyScroll, isMobile
} from '../config/utils';
import { appLogo } from '../../app/logo/logo';
import { SvgIcon } from '../sprites';

import 'dialog-polyfill/dialog-polyfill.css';

const renderBackArrow = ({ backArrowAction, backCustomAction }) => (
  backArrowAction || backCustomAction ? (
    <div className={ classes.arrowDiv } onClick={ backCustomAction || backArrowAction }>
      <SvgIcon id="back_arrow" className={ classes.arrow } />
    </div>
  ) : <div />
);

const renderRightItem = ({ rightCustomItem, closeButtonAction }) => {
  if (rightCustomItem) return rightCustomItem;
  return (
    closeButtonAction ? (
      <button data-testid="popup-close-btn" className={ classes.closeBtn } onClick={ closeButtonAction } type="button">
        <SvgIcon id="ic_chat_hangup" className={ classes.closeButtonImg } size={ 10 } />
      </button>
    ) : <div />
  );
};

const shouldRenderHeader = ({
  backArrowAction, closeButtonAction, rightCustomItem, backCustomAction
}) => backArrowAction || closeButtonAction || !!rightCustomItem || !!backCustomAction;

const renderTopHeader = ({
  customTopHeader, backArrowAction, backCustomAction, title, rightCustomItem, closeButtonAction
}) => {
  if (customTopHeader) return customTopHeader;
  if (!shouldRenderHeader({
    backArrowAction, closeButtonAction, rightCustomItem, backCustomAction
  })) return null;
  return (
    <div className={ classes.topHeaderConteiner }>
      {renderBackArrow({ backArrowAction, backCustomAction })}
      {title}
      {renderRightItem({ rightCustomItem, closeButtonAction })}
    </div>
  );
};

const handleSetEmptyDiv = ({ emptyHeight, setEmptyDivHeight }) => {
  if (isMobile()) {
    let updatedHeight = emptyHeight;
    if (emptyHeight < 0 && emptyHeight < 150) updatedHeight = Math.abs(updatedHeight) + 150;
    setEmptyDivHeight(Math.abs(updatedHeight));
    return;
  }
  if (emptyHeight < 0) {
    setEmptyDivHeight(Math.abs(emptyHeight));
    return;
  }
  setEmptyDivHeight(0);
};

const getBottomPopoverEmptyDivHeight = ({
  appGeneralPopupContentRef, appGeneralPopupOverlayContainerRef, setEmptyDivHeight
}) => {
  const appGeneralPopupContent = appGeneralPopupContentRef.current;
  const appGeneralPopupOverlayContainer = appGeneralPopupOverlayContainerRef;
  if (!appGeneralPopupContent || !appGeneralPopupOverlayContainer) return;
  const appGeneralPopupOverlayContainerPosition = appGeneralPopupOverlayContainer.getBoundingClientRect();
  const appGeneralPopupContentPosition = appGeneralPopupContent.getBoundingClientRect();
  const emptyHeight = appGeneralPopupOverlayContainerPosition.height - appGeneralPopupContentPosition.height;
  handleSetEmptyDiv({ emptyHeight, setEmptyDivHeight });
};

const getTopHeaderConteinerStyle = ({ withLogo }) => {
  if (withLogo) return { display: 'flex' };
  return {};
};

const renderTopHeaderContainer = ({ logoImg, withLogo }) => {
  if (logoImg) {
    return <div className={ classes.topHeaderConteinerLogoContainer } style={ { display: 'flex' } }>{logoImg}</div>;
  }
  return (
    <div
      className={ classes.topHeaderConteinerLogoContainer }
      style={ getTopHeaderConteinerStyle({ withLogo }) }
    >
      <img src={ appLogo() } alt="" className={ classes.topHeaderConteinerLogo } style={ getTopHeaderConteinerStyle({ withLogo }) } />
    </div>
  );
};

const getOverlayContainerClickHandler = ({ closable, e }) => {
  if (closable) closable(e);
};

const getGeneralContainerClickHandler = ({ closable, e }) => {
  if (closable) e.stopPropagation();
};

const resizeObserverHandler = ({ appGeneralPopupContent, handleResize }) => {
  if (appGeneralPopupContent) {
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(appGeneralPopupContent);
    return () => { resizeObserver.disconnect(); };
  }
  return () => { };
};

const bodyScrollHandler = ({ visible, handleResize }) => {
  if (visible) {
    hideBodyScroll();
    window.addEventListener('resize', handleResize, false);
  }
  if (!visible) {
    const overlayContainers = document.getElementsByClassName(`${ classes.overlayContainer }`);
    if (overlayContainers.length < 1) unlockBodyScroll();
    window.removeEventListener('resize', handleResize, false);
  }
};

const renderPopupContentHandler = ({
  visible, emptyDivHeight, appGeneralPopupContentRef, logoImg, title, customTopHeader,
  backArrowAction, backCustomAction, rightCustomItem, closeButtonAction, children, withLogo,
  getOverlayContainerClick, getGeneralContainerClick, appGeneralPopupOverlayContainerRefHandler
}) => {
  if (!visible) return null;

  return (
    <div
      id="appGeneralPopupOverlayContainer"
      className={ classes.overlayContainer }
      onClick={ getOverlayContainerClick }
      ref={ appGeneralPopupOverlayContainerRefHandler }
    >
      <div id="generalContainer" className={ classes.generalContainer } onClick={ getGeneralContainerClick }>
        <div className={ classes.generalHolder }>
          <div className={ classes.emptyDiv } onClick={ getOverlayContainerClick } style={ { '--height': `${ emptyDivHeight }px` } } />
          <div ref={ appGeneralPopupContentRef }>
            {renderTopHeaderContainer({ logoImg, withLogo })}
            <div className={ classes.popupContainer }>
              {renderTopHeader({
                customTopHeader, backArrowAction, backCustomAction,
                title, rightCustomItem, closeButtonAction
              })}
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function AppGeneralPopup({
  children,
  backArrowAction,
  closeButtonAction,
  rightCustomItem,
  backCustomAction,
  theme = appGeneralPopupTheme.DEFAULT,
  visible = true,
  title,
  customTopHeader,
  closable,
  logoImg,
  withLogo = false
}) {
  const appGeneralPopupContentRef = useRef();
  const dialogRef = useRef();
  const [appGeneralPopupOverlayContainerRef, setAppGeneralPopupOverlayContainerRef] = useState(null);
  const [emptyDivHeight, setEmptyDivHeight] = useState(null);

  const handleResize = debounce(() => {
    getBottomPopoverEmptyDivHeight({
      appGeneralPopupContentRef, appGeneralPopupOverlayContainerRef, setEmptyDivHeight
    });
  }, 100);

  useEffect(() => {
    if (dialogRef.current && !dialogRef.current.showModal) {
      dialogPolyfill.registerDialog(dialogRef.current);
    }
    if (visible && !dialogRef.current?.open) {
      dialogRef.current?.showModal();
    } else if (!visible && dialogRef.current?.open) {
      dialogRef.current?.close();
    }
  }, [visible, dialogRef.current]);

  useEffect(() => {
    const handleEscButton = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    dialogRef.current?.addEventListener('keydown', handleEscButton);

    return () => { dialogRef.current?.removeEventListener('keydown', handleEscButton); };
  }, [dialogRef.current]);

  useEffect(() => {
    if (visible) {
      bodyScrollHandler({ visible, handleResize });
      return () => { bodyScrollHandler({ visible: !visible, handleResize }); };
    }
    return () => { };
  }, [visible]);

  useEffect(() => {
    const appGeneralPopupContent = appGeneralPopupContentRef.current;
    const cleanerFunction = resizeObserverHandler({ appGeneralPopupContent, handleResize });
    if (appGeneralPopupOverlayContainerRef) {
      appGeneralPopupOverlayContainerRef.scrollTop = appGeneralPopupOverlayContainerRef.scrollHeight;
    }
    return () => { cleanerFunction(); };
  }, [appGeneralPopupOverlayContainerRef, appGeneralPopupContentRef.current]);

  const appGeneralPopupOverlayContainerRefHandler = useCallback((appGeneralPopupOverlayContainerNode) => {
    setAppGeneralPopupOverlayContainerRef(appGeneralPopupOverlayContainerNode);
  }, []);

  const getOverlayContainerClick = (e) => { getOverlayContainerClickHandler({ closable, e }); };

  const getGeneralContainerClick = (e) => { getGeneralContainerClickHandler({ closable, e }); };

  const renderPopupContent = () => (
    renderPopupContentHandler({
      visible, emptyDivHeight, appGeneralPopupContentRef,
      logoImg, title, customTopHeader, backArrowAction, backCustomAction, rightCustomItem,
      closeButtonAction, children, getOverlayContainerClick, getGeneralContainerClick, withLogo,
      appGeneralPopupOverlayContainerRefHandler
    })
  );

  if (!visible) return null;

  return (
    <>
      {createPortal(
        <dialog ref={ dialogRef } className={ classNames(classes.dialog, classes[theme]) }>{renderPopupContent()}</dialog>,
        document.body
      )}
    </>
  );
}

export default AppGeneralPopup;
